import { Link } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import SectionHeader from '../components/ui/SectionHeader.jsx';
import ProductCard from '../components/ui/ProductCard.jsx';
import SectionState from '../components/ui/SectionState.jsx';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';
import { getProducts } from '../api/productsApi.js';
import { getCategories } from '../api/catalogApi.js';
import { CATEGORY_LAYOUTS, CATEGORY_SPECIAL_TYPES, CATEGORY_DISPLAY } from '../data/categories.js';

function HomePage() {
  const { user } = useAuth();
  // 精選商品走分頁 API：取前 4 筆，解出 items 後維持陣列介面，下方渲染不變
  const { data: featured = [], loading: lf, error: ef, refetch: rf } = useApi(() => getProducts({ featured: true, limit: 4 }).then(r => r.items), []);
  const { data: categories = [], loading: lc, error: ec, refetch: rc } = useApi(() => getCategories(), []);

  return (
    <>
      <section className="hero">
        <svg className="hero-watermark" viewBox="0 0 200 600" aria-hidden="true">
          <use href="#dart-line" />
        </svg>
        <div className="hero-inner">
          <div>
            <span className="hero-tag">
              <span className="hero-tag-dot" />
              入荷新品 · NEW ARRIVALS
            </span>
            <h1 className="hero-title">擲出你的<em>道</em></h1>
            <p className="hero-title-en">DART YOUR WAY</p>
            <p className="hero-sub">從第一支標到選手款 — 道飛鏢工藝專賣，為每位選手找到屬於自己的標。</p>
            <div className="hero-ctas">
              <Link className="btn btn-primary" to="/shop">
                選購商品 <Icon name="arrow" size={16} />
              </Link>
              {/* 未登入才顯示加入會員 */}
              {!user && <Link className="btn btn-ghost" to="/login">加入會員 · 領 50 點</Link>}
            </div>
          </div>

          <div className="hero-art" aria-hidden="true">
            <div className="hero-art-ring"><div className="hero-art-ring-inner" /></div>
            <div className="hero-art-ring"><div className="hero-art-ring-dot" /></div>
            <span className="hero-art-target">
              <span className="hero-art-target-core" />
            </span>
            <svg className="hero-art-dart" viewBox="0 0 200 600"><use href="#dart-full" /></svg>
          </div>
        </div>
      </section>

      {/* Categories — 7 類 (5 部位 / 套組 + 2 特殊：虛擬商品、飛鏢機卡) */}
      <section className="section">
        <SectionHeader title="七大商品分類" linkTo="/shop" linkText="全部分類" />
        {ec ? (
          <SectionState error={ec} onRetry={rc} />
        ) : (!lc && categories.length === 0) ? (
          <SectionState onRetry={rc} />
        ) : (
          <div className="parts">
            {categories.map(cat => {
              const display = CATEGORY_DISPLAY[cat.id] || {};
              return display.layout === CATEGORY_LAYOUTS.SPECIAL
                ? <CategorySpecialCard cat={cat} key={cat.id} />
                : <CategoryNormalCard cat={cat} key={cat.id} />;
            })}
          </div>
        )}
      </section>

      {/* Featured Products — 由 API 動態取得 */}
      <section className="section" id="products">
        <SectionHeader title="熱門商品" linkTo="/shop" linkText="全部商品" />
        {ef ? (
          <SectionState error={ef} onRetry={rf} />
        ) : lf ? (
          <div className="product-grid">
            {Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        ) : featured.length === 0 ? (
          <SectionState onRetry={rf} />
        ) : (
          <div className="product-grid">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* 最新資訊（News）區塊暫時隱藏——功能未做（見 TODOS.md）。之後要做再加回。 */}
    </>
  );
}

function ProductCardSkeleton() {
  return (
    <div className="product" style={{ minHeight: 320 }}>
      <div className="product-image" style={{ background: 'var(--ice)' }} />
      <div style={{ height: 16, background: 'var(--ice)', borderRadius: 4, margin: '14px 0' }} />
      <div style={{ height: 16, background: 'var(--ice)', borderRadius: 4, width: '60%' }} />
    </div>
  );
}

function CategoryNormalCard({ cat }) {
  const { tone, num, svg, viewBox, svgHeight } = CATEGORY_DISPLAY[cat.id] || {};
  return (
    <Link to={`/shop?category=${cat.id}`} className="part" data-id={cat.id} data-tone={tone}>
      <span className={`part-num ${tone}`}>{num}</span>
      <h3 className="part-name">{cat.name}</h3>
      <span className="part-en">{cat.en}</span>
      <div className="part-img-wrap">
        <svg className="part-img" viewBox={viewBox} style={{ height: svgHeight }}>
          <use href={`#${svg}`} />
        </svg>
      </div>
      <span className="part-cta"><Icon name="arrow" size={14} /></span>
    </Link>
  );
}

function CategorySpecialCard({ cat }) {
  const { num, badge, specialType, linkTo } = CATEGORY_DISPLAY[cat.id] || {};
  if (specialType === CATEGORY_SPECIAL_TYPES.VIRTUAL) {
    return (
      <Link to={linkTo} className="part special-virtual" data-id={cat.id}>
        <div className="part-grid-bg" />
        {badge && <span className="part-badge">{badge}</span>}
        <span className="part-num">{num}</span>
        <h3 className="part-name">{cat.name}</h3>
        <span className="part-en">{cat.en}</span>
        <div className="part-theme-stack">
          <span className="part-theme-chip d2"><span className="dot" />DARTSLIVE 2</span>
          <span className="part-theme-chip d3"><span className="dot" />DARTSLIVE 3</span>
          <span className="part-theme-chip fun"><span className="dot" />FUN DART</span>
        </div>
        <span className="part-cta"><Icon name="arrow" size={14} /></span>
      </Link>
    );
  }
  // dart-card (#07)
  return (
    <Link to={linkTo} className="part special-dart-card" data-id={cat.id}>
      <div className="part-text-col">
        <span className="part-num">{num}</span>
        <h3 className="part-name">{cat.name}</h3>
        <span className="part-en">{cat.en}</span>
        <p className="desc">DARTSLIVE / PHOENICA 機台卡</p>
        <div className="part-perks">
          <div><Icon name="check" size={14} /> 特殊卡面</div>
          <div><Icon name="check" size={14} /> 限定主題</div>
          <div><Icon name="check" size={14} /> 限定特效</div>
        </div>
      </div>
      <div className="card-preview">
        <div className="card-preview-top">
          <span className="card-preview-brand">MICHIDART</span>
          <span className="card-preview-chip" />
        </div>
        <div className="card-preview-no">1234 5678 9012 3456</div>
        <div className="card-preview-bot">
          {/* <span className="card-preview-name">CHEN CHIA-WEI</span> */}
          {/* <span className="card-preview-tier">GOLD</span> */}
        </div>
      </div>
      <span className="part-cta"><Icon name="arrow" size={14} /></span>
    </Link>
  );
}

export default HomePage;
